import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { activateOnEnter } from '@src/components/activateOnEnter';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { Typography } from '@src/components/Typography/Typography';
import './Checkbox.css';

type NativeCheckboxProps = Omit<
  ComponentPropsWithRef<'input'>,
  'style' | 'type' | 'checked' | 'onChange' | 'value' | 'children'
>;

export interface CheckboxProps extends NativeCheckboxProps {
  /** Controlled, like every other control here. */
  checked: boolean;
  /** State-first: callers want to know what it became, not the event. */
  onCheckedChange: (checked: boolean) => void;
  /**
   * Sits beside the box, and is part of the click target. A node rather than a
   * string, because consent copy usually contains a link.
   */
  label?: ReactNode;
  /** Quiet note under the row, aligned with the label. */
  hint?: string;
  /** Turns the box red and replaces the hint with the message. */
  error?: string;
  disabled?: boolean;
  /**
   * Take only the width the label needs. The default fills the row, which is
   * what a form wants; a checkbox sharing a line with another control does not.
   */
  fit?: boolean;
  /** Parent id. The input, label and message hang off it. */
  testId?: string;
  /**
   * Enter confirms: checks if needed (never unchecks) and continues. Space
   * still toggles — that is the native checkbox contract.
   */
  onEnter?: () => void;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  hint,
  error,
  disabled = false,
  fit = false,
  className,
  id,
  onKeyDown,
  onEnter,
  testId,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  const classes = ['checkbox'];
  if (error) {
    classes.push('checkbox--invalid');
  }
  if (fit) {
    classes.push('checkbox--fit');
  }
  if (disabled) {
    classes.push('checkbox--disabled');
  }
  if (className) {
    classes.push(className);
  }

  // An error is the more urgent of the two, and stacking both under a one-line
  // control makes the row taller than the thing it describes.
  const message = error ?? hint;

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      {/* Wrapping the box and the label in the <label> makes the text part of the
          hit target, and means no htmlFor is needed. */}
      <label className="checkbox__row">
        <input
          className="checkbox__input"
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={message ? messageId : undefined}
          onChange={(event) => onCheckedChange(event.target.checked)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              if (!checked) {
                flushSync(() => {
                  onCheckedChange(true);
                });
              }
              onEnter();
              onKeyDown?.(event);
              return;
            }
            activateOnEnter(event);
            onKeyDown?.(event);
          }}
          {...testIdAttr(testIdFor(testId, 'control'))}
          {...rest}
        />

        {/* Decorative: the input above carries the state, the label carries the
            name. Everything visible is drawn here. */}
        <span className="checkbox__box" aria-hidden="true">
          <CheckIcon className="checkbox__check" />
        </span>

        {label && (
          <Typography
            as="span"
            role="label/control"
            className="checkbox__label"
            {...testIdAttr(testIdFor(testId, 'label'))}
          >
            {label}
          </Typography>
        )}
      </label>

      {message && (
        <Typography
          as="p"
          role={error ? 'meta/error' : 'meta/helper'}
          className="checkbox__message"
          id={messageId}
          {...testIdAttr(testIdFor(testId, error ? 'error' : 'hint'))}
        >
          {message}
        </Typography>
      )}
    </div>
  );
}
